/**
 * ocsBasis — the DXF "Arbitrary Axis Algorithm" (Autodesk DXF reference;
 * `ezdxf.math.OCS`), extracted as the ONE-TRUTH basis computation shared by
 * every OCS/extrusion-normal consumer in apps/web.
 *
 * Given a unit normal N = (nx, ny, nz) (an entity's extrusion / a
 * Rectangular Associative Array's base-plane normal), DXF derives the OCS
 * X / Y axes so the basis is reproducible (otherwise an arbitrary in-plane
 * rotation around N would be ambiguous):
 *
 *   - if |nx| < 1/64 AND |ny| < 1/64:  Ax = WY × N   (WY = world Y = (0,1,0))
 *   - else                             Ax = WZ × N   (WZ = world Z = (0,0,1))
 *   then normalise Ax, and Ay = N × Ax (already unit since N ⟂ Ax).
 *
 * Originally lived only inside `print/pdf/converters/ocs.ts` (the PDF
 * exporter's OCS→WCS-XY planar-projection lift); extracted here so a SECOND
 * consumer (scene3d's ARRAYRECT Z-level stacking, S-ARRAYRECT-BASEPLANE-
 * NORMAL) can share the identical basis math instead of hand-rolling a
 * second copy of the threshold + cross-product logic (jscpd DRY gate — one
 * domain truth, two call sites).
 */

import type { DrawingPoint3D } from "@dxf-ai-editor/drawing-model";

import { cross, normalize, type Vec3 } from "./vec3";

/** 1/64 — the DXF Arbitrary Axis Algorithm near-Z threshold. */
const ARBITRARY_AXIS_THRESHOLD = 1 / 64;

/**
 * Whether `normal` is the default (0,0,1) WCS Z (within a small tolerance),
 * i.e. an entity/array authored flat in the WCS XY plane — the
 * overwhelmingly common case, where every OCS basis consumer can skip the
 * transform entirely (identity fast-path, byte-identical to pre-OCS-aware
 * behaviour).
 *
 * A normal of (0,0,k) with k > 0 (any positive-Z, non-unit extrusion) is
 * also treated as default: its in-plane basis matches the WCS XY axes.
 */
export function isDefaultOcsNormal(normal: DrawingPoint3D | undefined): boolean {
  if (!normal) return true;
  const { x, y, z } = normal;
  return Math.abs(x) < 1e-9 && Math.abs(y) < 1e-9 && z > 0;
}

export type OcsBasis = {
  /** OCS X axis, unit length, expressed in WCS. */
  ax: Vec3;
  /** OCS Y axis, unit length, expressed in WCS. */
  ay: Vec3;
  /** OCS Z axis (the normalised input normal), expressed in WCS. */
  n: Vec3;
};

/**
 * Compute the full 3-axis OCS basis for `normal` via the Arbitrary Axis
 * Algorithm. Returns the WCS-aligned identity basis for a default / absent /
 * degenerate (zero-length) normal — never NaN, so a corrupt extrusion never
 * blanks the dependent geometry.
 */
export function computeOcsBasis(normal: DrawingPoint3D | undefined): OcsBasis {
  if (isDefaultOcsNormal(normal)) {
    return { ax: { x: 1, y: 0, z: 0 }, ay: { x: 0, y: 1, z: 0 }, n: { x: 0, y: 0, z: 1 } };
  }
  const n = normalize({ x: normal!.x, y: normal!.y, z: normal!.z });
  if (n.x === 0 && n.y === 0 && n.z === 0) {
    // Degenerate (zero-length) normal — identity fallback, never NaN.
    return { ax: { x: 1, y: 0, z: 0 }, ay: { x: 0, y: 1, z: 0 }, n: { x: 0, y: 0, z: 1 } };
  }
  const ax =
    Math.abs(n.x) < ARBITRARY_AXIS_THRESHOLD && Math.abs(n.y) < ARBITRARY_AXIS_THRESHOLD
      ? normalize(cross({ x: 0, y: 1, z: 0 }, n)) // WY × N
      : normalize(cross({ x: 0, y: 0, z: 1 }, n)); // WZ × N
  const ay = cross(n, ax); // already unit (N ⟂ Ax)
  return { ax, ay, n };
}

/**
 * Lift a point authored in OCS `(x, y, elev)` (elevation along the OCS Z /
 * the plane normal) into full WCS `{x, y, z}` — `x·Ax + y·Ay + elev·N`.
 */
export function ocsToWcs3d(basis: OcsBasis, x: number, y: number, elev = 0): Vec3 {
  return {
    x: x * basis.ax.x + y * basis.ay.x + elev * basis.n.x,
    y: x * basis.ax.y + y * basis.ay.y + elev * basis.n.y,
    z: x * basis.ax.z + y * basis.ay.z + elev * basis.n.z,
  };
}
